import _ from "lodash";
import { ATTENDANCE_CONSTANTS } from "AttendanceDashboard/constants/stringConstants";
import moment from "moment";
import {
  PARENT,
  STAFF,
  FIRST_NAME,
  LAST_NAME,
  DEFAULT_NAME_ORDER_PREFERENCE,
  STUDENT,
  MAY,
  OLD_SUBJECT_BASED_GRADING,
  COURSE_BASED_GRADING,
  SUBJECT_BASED_GRADING,
} from "Constants/stringConstants";
import {
  ENABLE_STUDENT_MIDDLE_NAME,
  ENABLE_USER_ACADEMIC_YEAR_ARCHIVAL_STATUS,
} from "Constants/permissionConstants";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import ACLStore from "lib/aclStore";
import {
  getPlatformUserDetailsFromCache,
  getOrganizationDetailsFromCache,
  getOrganizationConstantsFromCache,
} from "modules/CommonGraphqlHelpers";
import { getCourseDetailsFromCache } from "Course/modules/CourseGraphqlHelpers";
import { getChildAcademicYearsFromCache } from "./CommonGraphqlHelpers";
import { store } from "store/createStore";
import { getIsIBISUIRevampEnabled } from "Utils/ACLStoreUtils";
import { getFamilySelectedChildYearGroupQueryFromCache } from "Platform/modules/PlatformGraphqlHelpers";

const getUserId = state => {
  const userInfo = getUserInfo();
  return userInfo.id;
};

const getOrganizationId = state => {
  const userInfo = getUserInfo();
  return userInfo.org_id;
};

const getCurrentAcademicYear = ({ organizationId }) => {
  const organizationDetails = getOrganizationDetailsFromCache(organizationId);
  const academicYears = _.get(organizationDetails, "academicYears", []);
  return _.find(academicYears, { isCurrentAcademicYear: true });
};

const getAcademicYearStartDate = ({ organizationId }) => {
  const currentAcademicYear = getCurrentAcademicYear({ organizationId });
  return _.get(currentAcademicYear, "startDate");
};

const getAcademicYearEndDate = ({ organizationId }) => {
  const currentAcademicYear = getCurrentAcademicYear({ organizationId });
  return _.get(currentAcademicYear, "endDate");
};

export const getNextOrLastAcademicYear = ({ organizationId }) => {
  const organizationDetails = getOrganizationDetailsFromCache(organizationId);
  const academicYears = _.sortBy(
    _.get(organizationDetails, "academicYears", []),
    "endDate"
  );
  const nextUpcomingAcademicYear = _.find(academicYears, {
    isUpcomingAcademicYear: true,
  });
  return nextUpcomingAcademicYear
    ? nextUpcomingAcademicYear
    : getCurrentAcademicYear({ organizationId });
};

const getNextOrLastAcademicYearEndDate = ({ organizationId }) => {
  const currentAcademicYear = getNextOrLastAcademicYear({ organizationId });
  return _.get(currentAcademicYear, "endDate");
};

const getUserEntityType = state => {
  const userInfo = getUserInfo();
  return userInfo.userEntityType;
};

const getUserType = state => {
  const userInfo = getUserInfo();
  return userInfo.user_type;
};

const getIsStaff = state => {
  return getUserType(state) === STAFF;
};

const getIsParent = state => {
  return getUserType(state) === PARENT;
};
const getIsStudent = () => {
  const userInfo = getUserInfo();
  return userInfo.user_type === STUDENT;
};

const getSelectedCurriculumId = state => {
  return _.get(state, "platform.currentCurriculumProgram.id", undefined);
};

const getSelectedCurriculumLabel = state => {
  return _.get(state, "platform.currentCurriculumProgram.label", "");
};

const getActiveTab = state => {
  return _.get(state, "login.activeTab");
};

const getOrganizationCurriculumPrograms = state => {
  return _.get(state, "platform.organizationCurriculumPrograms", []);
};

const getAccountCurriculumPrograms = state => {
  return _.get(state, "platform.accountCurriculumPrograms", []);
};

const getSchoolCurriculumPrograms = state => {
  return _.get(state, "platform.schoolCurriculumPrograms", []);
};

const getAcademicCurriculumPrograms = state => {
  return _.get(state, "platform.academicCurriculumPrograms", []);
};

const getSelectedCurriculumProgram = state => {
  const selectedCurriculumProgramId = getSelectedCurriculumId(state);
  const organizationCurriculumPrograms = getOrganizationCurriculumPrograms(
    state
  );

  return _.find(
    organizationCurriculumPrograms,
    ({ id }) => id == selectedCurriculumProgramId
  );
};

const getSelectedAcademicYear = state => {
  const selectedAcademicYear = _.get(state, "platform.academicYearSelected");

  if (!selectedAcademicYear) {
    const organizationId = getOrganizationId(state);
    return getCurrentAcademicYear({ organizationId });
  }

  return selectedAcademicYear;
};

const getSelectedAcademicYearId = state => {
  const selectedAcademicYear = getSelectedAcademicYear(state);
  return _.get(selectedAcademicYear, "id", null);
};

const getSelectedAcademicYearStartDate = ({ selectedAcademicYear }) => {
  return moment(_.get(selectedAcademicYear, "startDate", "")).format(
    ATTENDANCE_CONSTANTS.YYYY_MM_DD
  );
};

const getSelectedAcademicYearEndDate = ({ selectedAcademicYear }) => {
  return moment(_.get(selectedAcademicYear, "endDate", "")).format(
    ATTENDANCE_CONSTANTS.YYYY_MM_DD
  );
};

const getCurrentCurriculumProgramType = state => {
  return _.get(state, "platform.currentCurriculumProgram.type");
};

const getCurriculumProgramIdForProgramType = ({
  isAccountLevel,
  accountCurriculumProgramId,
  curriculumPrograms,
}) => {
  const curriculumProgramId = isAccountLevel
    ? _.get(
        _.find(
          curriculumPrograms,
          ({ programType }) => programType === "ACCOUNT"
        ),
        "id"
      )
    : accountCurriculumProgramId;

  return curriculumProgramId;
};

const getSelectedCurriculumAcronym = state => {
  return _.get(state, "platform.currentCurriculumProgram.acronym", "");
};

const getAcademicSetupTypeForCurriculum = (state, id) => {
  const organizationCurriculumPrograms = getOrganizationCurriculumPrograms(
    state
  );
  const curriculum = _.find(organizationCurriculumPrograms, ["id", id]);

  return _.get(curriculum, "academicSetupType", null);
};

export const getCurrentAcademicSetupTypeFromState = state => {
  const academicSetupType =
    _.get(state, "platform.currentCurriculumProgram.academicSetupType", null) ||
    OLD_SUBJECT_BASED_GRADING;

  const isSubjectBasedGrading = academicSetupType === SUBJECT_BASED_GRADING;
  const isCourseBasedGrading = academicSetupType === COURSE_BASED_GRADING;
  const isOldSubjectBasedGrading =
    academicSetupType === OLD_SUBJECT_BASED_GRADING;

  const isOldOrNewSubjectBasedGrading =
    isSubjectBasedGrading || isOldSubjectBasedGrading;

  return {
    academicSetupType,
    isSubjectBasedGrading,
    isCourseBasedGrading,
    isOldSubjectBasedGrading,
    isOldOrNewSubjectBasedGrading,
  };
};

const getDefaultNameSortPreference = state => {
  return _.get(state, "platform.defaultNameSortPreference", FIRST_NAME);
};

const getDefaultStudentNameOrderPreference = state => {
  const isStudentMiddleNameEnabled = ACLStore.can(ENABLE_STUDENT_MIDDLE_NAME);
  const defaultStudentNameSortPreference = _.get(
    state,
    "platform.defaultNameSortPreference",
    FIRST_NAME
  );
  const defaultStudentNameSortValue =
    defaultStudentNameSortPreference === LAST_NAME
      ? _.reverse(_.cloneDeep(DEFAULT_NAME_ORDER_PREFERENCE))
      : DEFAULT_NAME_ORDER_PREFERENCE;
  return isStudentMiddleNameEnabled
    ? _.get(state, "platform.defaultStudentNameOrderPreference", [
        FIRST_NAME,
        LAST_NAME,
      ])
    : defaultStudentNameSortValue;
};

const getDefaultStudentIdentifierNamePreference = state => {
  return _.get(
    state,
    "platform.defaultStudentIdentifierNamePreference",
    FIRST_NAME
  );
};

const getChildId = state => {
  const userInfo = getUserInfo();
  return userInfo.childID;
};

const getIsChildBlocked = () => {
  const userInfo = getUserInfo();
  return _.get(userInfo, "isChildBlocked", false);
};

const getChildProfileImage = () => {
  const userInfo = getUserInfo();
  return _.get(userInfo, "childProfileImage", "");
};

const getOrganizationName = () => {
  const userInfo = getUserInfo();
  return _.get(userInfo, "org_name", "");
};

const getUserCurriculumPrograms = state => {
  const userInfo = getUserInfo();
  return userInfo.userCurriculumPrograms;
};

const getDemoOrganizationCurriculumProgram = state => {
  const curriculumType = _.get(
    state,
    ["platform", "currentCurriculumProgram", "type"],
    ""
  );

  const sampleOrganizationPortalType = _.get(state, [
    "platform",
    "sampleOrgPortalType",
  ]);

  const curriculumProgramId = _.get(
    state,
    ["platform", "demoOrgCurriculumId", curriculumType],
    null
  );

  if (!curriculumProgramId || _.isEmpty(sampleOrganizationPortalType)) {
    return null;
  }

  const organizationCurriculumPrograms = _.get(
    state,
    ["platform", "organizationCurriculumPrograms"],
    []
  );

  const nodeInterfaceType = _.get(
    _.find(organizationCurriculumPrograms, {
      type: curriculumType,
    }),
    "nodeInterfaceType",
    ""
  );

  return {
    id: curriculumProgramId,
    nodeInterfaceType,
    type: curriculumType,
  };
};

const getSelectedCourseId = state => {
  return _.get(state, "teacher.selected_class.selected_course");
};

const getSelectedCourseName = state => {
  const course = getCourseDetailsFromCache({
    id: getSelectedCourseId(state),
    courseGradeFilters: { academicYearId: getSelectedAcademicYearId(state) },
  });
  return _.get(course, "title", "");
};

const getIsCurrentAcademicYearSelected = state => {
  const academicYearSelected = _.get(
    state,
    "platform.academicYearSelected",
    null
  );
  return _.get(academicYearSelected, "isCurrentAcademicYear");
};

const getAcademicYearForCurriculumProgram = (
  state,
  { curriculumProgramId } = {}
) => {
  const selectedAcademicYear = getSelectedAcademicYear(state);

  const selectedCurriculumProgram = curriculumProgramId
    ? _.find(_.get(state, "platform.organizationCurriculumPrograms", []), {
        id: curriculumProgramId,
      })
    : getSelectedCurriculumProgram(state);

  const childAcademicYearsDetails = getChildAcademicYearsFromCache({
    id: selectedAcademicYear.id,
  });

  const academicYear = _.get(
    _.find(
      _.get(childAcademicYearsDetails, "childAcademicYears", []),
      ({ curriculumProgram }) => {
        return curriculumProgram.id === selectedCurriculumProgram?.id;
      }
    ),
    "academicYear",
    null
  );

  //Fallback for 1.0 accounts
  return academicYear ?? selectedAcademicYear;
};

const getIsFutureAcademicYearSelected = state => {
  const academicYearSelected = _.get(
    state,
    "platform.academicYearSelected",
    null
  );
  return _.get(academicYearSelected, "isUpcomingAcademicYear");
};

export const getIsFutureAcademicYearSelectedForCurriculumProgram = (
  state,
  { readCurriculumProgramFromRedux = true, curriculumProgramId } = {}
) => {
  const selectedAcademicYear = getAcademicYearForCurriculumProgram(state, {
    readCurriculumProgramFromRedux,
    curriculumProgramId,
  });

  return _.get(selectedAcademicYear, "isUpcomingAcademicYear");
};

const getIsPreviousAcademicYearSelected = state => {
  const academicYearSelected = _.get(
    state,
    "platform.academicYearSelected",
    null
  );
  return (
    !_.get(academicYearSelected, "isCurrentAcademicYear") &&
    !_.get(academicYearSelected, "isUpcomingAcademicYear")
  );
};

const getCourseId = state => {
  return _.get(state, "teacher.selected_class.selected_course", "");
};

const getAcademicYearId = state => {
  return _.get(state, "platform.academicYearSelected.id", "");
};

const getSelectedAcademicYearObject = state => {
  return _.get(state, "platform.academicYearSelected", {});
};

const getIsCurrentAndSelectedAcademicYearSame = state => {
  const organizationId = getOrganizationId(state);

  const selectedAcademicYearId = getSelectedAcademicYearId(state);

  const currentAcademicYear = getCurrentAcademicYear({
    organizationId: organizationId,
  });

  const currentAcademicYearId = _.get(currentAcademicYear, "id", "");

  return _.isEqual(selectedAcademicYearId, currentAcademicYearId);
};

export const getAcademicYearsOfCurriculumProgram = (
  state,
  { curriculumProgramId } = {}
) => {
  const curriculumProgram = curriculumProgramId
    ? _.find(_.get(state, "platform.organizationCurriculumPrograms", []), {
        id: curriculumProgramId,
      })
    : getSelectedCurriculumProgram(state);

  const academicYears = _.get(curriculumProgram, "academicYears", []);

  //Fallback when AY are not found due to missing curriculum program id
  if (_.isEmpty(academicYears)) {
    const organizationId = getOrganizationId(state);

    const organizationDetails = getOrganizationDetailsFromCache(organizationId);
    return _.get(organizationDetails, "academicYears", []);
  }

  return academicYears;
};

export const getCurrentAcademicYearOfCurriculumProgram = (
  state,
  { curriculumProgramId } = {}
) => {
  const academicYears = getAcademicYearsOfCurriculumProgram(state, {
    curriculumProgramId,
  });
  return _.find(academicYears, year => {
    return year.isCurrentAcademicYear;
  });
};

export const getIsCurrentAndSelectedAcademicYearOfCurruiculumProgramSame = (
  state,
  { curriculumProgramId } = {}
) => {
  const selectedAcademicYear = getAcademicYearForCurriculumProgram(state, {
    curriculumProgramId,
  });

  const selectedAcademicYearId = _.get(selectedAcademicYear, "id", "");

  const currentAcademicYear = getCurrentAcademicYearOfCurriculumProgram(state, {
    curriculumProgramId,
  });

  const currentAcademicYearId = _.get(currentAcademicYear, "id", "");
  return _.isEqual(selectedAcademicYearId, currentAcademicYearId);
};

export const getAllAcademicYearsOfAllCurriculumPrograms = ({
  curriculumIds,
}) => {
  const state = store.getState();

  const organizationCurriculumPrograms = getOrganizationCurriculumPrograms(
    state
  );

  const curriculumProgramIds =
    curriculumIds ??
    _.map(
      organizationCurriculumPrograms,
      curriculumProgram => curriculumProgram.id
    );

  const allEntityPairs = _.flatMap(
    curriculumProgramIds,
    curriculumProgramId => {
      const academicYears = getAcademicYearsOfCurriculumProgram(state, {
        curriculumProgramId,
      });
      return _.map(academicYears, academicYear => ({
        curriculumProgramId,
        academicYearId: _.get(academicYear, "id", ""),
      }));
    }
  );

  // Ensure unique pairs of curriculumProgramId and academicYearId
  const uniqueEntityPairs = _.uniqBy(
    allEntityPairs,
    pair => `${pair.curriculumProgramId}_${pair.academicYearId}`
  );

  return _.map(uniqueEntityPairs, ({ curriculumProgramId, academicYearId }) => [
    {
      entityId: curriculumProgramId,
      entityType: "CURRICULUM_PROGRAM",
    },
    {
      entityId: academicYearId,
      entityType: "ACADEMIC_YEAR",
    },
  ]);
};

const getIsCommunity = state => {
  return getActiveTab(state) == "community";
};

const getIsArchivedClass = state => {
  return _.get(state, "teacher.selected_class.isArchivedClass", false);
};

const getIsUserOnline = state => {
  return _.get(state, "app_services.isOnline", false);
};

const getUserArchivalStatus = user => {
  return ACLStore.can(ENABLE_USER_ACADEMIC_YEAR_ARCHIVAL_STATUS)
    ? _.get(user, "isArchivedV2", false)
    : _.get(user, "isArchived", false);
};

const getImmediatePreviousAcademicYear = ({
  academicYears,
  selectedAcademicYear,
}) => {
  const previousAcademicYears = _.sortBy(
    _.filter(academicYears, year =>
      moment(year.endDate).isBefore(moment(selectedAcademicYear.startDate))
    ),
    year => moment(year.endDate)
  );
  const previousAcademicYear = _.last(previousAcademicYears);
  return previousAcademicYear;
};

const getImmediateFutureAcademicYear = () => {
  const organizationId = getOrganizationId();
  const organizationDetails = getOrganizationDetailsFromCache(organizationId);
  const academicYearsList = _.get(organizationDetails, "academicYears", []);
  const academicYearListInOrder = _.reverse([...academicYearsList]);

  const immediateFutureAcademicYear = _.find(academicYearListInOrder, {
    isUpcomingAcademicYear: true,
  });
  return immediateFutureAcademicYear;
};

const getCurrentCurriculumContextForGlobalSearch = state => {
  const isGlobalSearchJumpToRevampEnabled = ACLStore.can(
    "FeatureFlag:EnableGlobalSearchJumpToRevamp"
  );

  if (isGlobalSearchJumpToRevampEnabled) {
    // if new revamp flag is enabled - the context would be determined by global search redux state
    return (
      _.get(state, "app_services.globalSearchBar.selectedCurriculum") ?? {}
    );
  } else {
    // else it will be platform selected curriculum context
    return getSelectedCurriculumProgram(state);
  }
};

const getCurrentCurriculumContextForJumpTo = state => {
  const isGlobalSearchJumpToV2Enabled = ACLStore.can(
    "FeatureFlag:EnableGlobalSearchJumpToV2"
  );

  if (isGlobalSearchJumpToV2Enabled) {
    return _.get(state, "app_services.jumpTo.selectedCurriculum", {});
  } else {
    return getCurrentCurriculumContextForGlobalSearch(state);
  }
};

const getSelectedGrades = state => {
  return _.get(state, "teacher.selected_class.selected_grades", []);
};

const getCurrentPlannerElementSets = state => {
  return _.get(state, "platform.currentPlannerElementSets", {});
};

const getIsUserImpersonated = state => {
  const platformUserInfo = getUserInfo({ portalType: "PLANNER" });

  const userObj = getPlatformUserDetailsFromCache({
    id: platformUserInfo.id,
    type: _.toUpper(platformUserInfo.user_type),
  });

  return _.get(userObj, "isImpersonated", false);
};

const getCurriculumProgramIdFromCurrentAdminNavigationNode = state => {
  const { nodes, childId: adminNavigationChildId } = _.get(
    state,
    "adminNavigation",
    {}
  );
  const node = _.find(nodes, { id: adminNavigationChildId });
  if (node) {
    return _.get(node, "curriculumProgramId");
  }

  return null;
};

export const getIsSchoolPortal = state => {
  return _.get(state, "login.activeTab") === "schoolSettingsTab";
};

export const getIsAccountOrSchoolPortal = state => {
  const activeTab = _.get(state, "login.activeTab");

  const isAccountOrSchoolPortal = _.includes(
    ["accountSettingsTab", "schoolSettingsTab"],
    activeTab
  );

  return isAccountOrSchoolPortal;
};

export const getIsAdminPortal = state => {
  const loginActiveTab = _.get(state, "login.activeTab");
  const isAccountOrSchoolPortal = getIsAccountOrSchoolPortal(state);

  return loginActiveTab === "admin" || isAccountOrSchoolPortal;
};

const getActivePageHeaderTab = state => {
  const { activePageHeaderTab = "" } = _.get(
    state,
    "dpExamReg.ibisRegistration",
    {}
  );
  return activePageHeaderTab;
};

const getIBISRegistrationActiveScreenName = state => {
  const activePageHeaderTab = getActivePageHeaderTab(state);

  if (getIsIBISUIRevampEnabled()) {
    if (activePageHeaderTab === "PREPARE_CANDIDATE_DATA") {
      return _.get(state, "dpExamReg.ibisRegistration.activeScreenName", "");
    }

    return activePageHeaderTab || "";
  }

  return _.get(state, "administrator.routes.childId", "");
};

const getIBISRegistrationScreenId = state => {
  if (!getIsIBISUIRevampEnabled()) {
    return _.get(state, "administrator.routes.childId", "");
  }

  const activeScreenName = getIBISRegistrationActiveScreenName(state);

  const { activeTab = "" } = _.get(state, "dpExamReg.ibisRegistration", {});

  if (activeScreenName === "REGISTER_CANDIDATES") {
    return _.join([activeScreenName, activeTab], "-");
  }

  return activeScreenName;
};

export const parseAdminMenuId = id => {
  if (!id) return {};

  if (_.includes(id, ":")) {
    const [curriculumProgramId, nodeId] = _.split(id, ":");
    return { curriculumProgramId, nodeId };
  } else return { nodeId: id };
};

const getCurriculumProgramIdFromRoute = state => {
  let _curriculumProgramId = null;

  const loginActiveTab = _.get(state, "login.activeTab");
  const isAdminPortal = loginActiveTab === "admin";

  const isAdminNavigationRevampPortal = _.includes(
    ["accountSettingsTab", "schoolSettingsTab"],
    loginActiveTab
  );

  if (isAdminPortal) {
    const { subChildId } = _.get(state, "administrator.routes", {});
    const { curriculumProgramId } = parseAdminMenuId(subChildId);

    _curriculumProgramId = curriculumProgramId;
  }

  if (isAdminNavigationRevampPortal) {
    _curriculumProgramId = getCurriculumProgramIdFromCurrentAdminNavigationNode(
      state
    );
  }

  return _curriculumProgramId;
};

const getSelectedPrimaryGradeIdForLearningCourse = () => {
  const state = store.getState();
  return _.get(
    state,
    "learningCourse.selectedLearningCourse.primaryGradeId",
    undefined
  );
};

const getSelectedPrimaryGradeIdForCourse = () => {
  const state = store.getState();
  const primaryGrade = _.get(state, "teacher.selected_class.primaryGrade", "");
  return _.get(primaryGrade, "id", undefined);
};

const getSelectedCurriculumExamSession = () => {
  const state = store.getState();
  const selectedCurriculumProgram = getSelectedCurriculumProgram(state);
  return _.get(selectedCurriculumProgram, "examSessionMonth");
};

const getIsExamSessionBasedOnCurriculum = () => {
  const examSession = getSelectedCurriculumExamSession();
  return examSession ? true : false;
};

const getExamSessionMonth = () => {
  const curriculumMonth = getSelectedCurriculumExamSession();
  if (curriculumMonth) {
    return curriculumMonth;
  }

  const state = store.getState();
  const organizationId = getOrganizationId(state);
  const constants = getOrganizationConstantsFromCache(organizationId);
  return _.get(constants, "examSessionMonth", MAY);
};

const getUserPrimaryGradeId = () => {
  const state = store.getState();
  const isParent = getIsParent(state);

  if (isParent) {
    const childId = getChildId(state);
    const childrenYearGroupGrade = getFamilySelectedChildYearGroupQueryFromCache(
      {
        id: childId,
      }
    );

    const primaryGrade = _.get(
      childrenYearGroupGrade,
      "node.yearGroup.grade.id",
      null
    );

    return primaryGrade;
  }

  const userData = getPlatformUserDetailsFromCache({
    id: getUserId(),
    type: getUserEntityType(),
  });

  return _.get(userData, "yearGroup.grade.id", null);
};

export {
  getCurrentAcademicYear,
  getUserId,
  getUserType,
  getIsStaff,
  getIsParent,
  getIsStudent,
  getUserEntityType,
  getOrganizationId,
  getActiveTab,
  getAcademicYearStartDate,
  getAcademicYearEndDate,
  getSelectedCurriculumId,
  getCurriculumProgramIdForProgramType,
  getSelectedCurriculumProgram,
  getSelectedAcademicYear,
  getSelectedAcademicYearId,
  getSelectedAcademicYearStartDate,
  getSelectedAcademicYearEndDate,
  getCurrentCurriculumProgramType,
  getSelectedCurriculumAcronym,
  getAcademicSetupTypeForCurriculum,
  getDefaultNameSortPreference,
  getDefaultStudentNameOrderPreference,
  getDefaultStudentIdentifierNamePreference,
  getOrganizationCurriculumPrograms,
  getAccountCurriculumPrograms,
  getSchoolCurriculumPrograms,
  getAcademicCurriculumPrograms,
  getChildId,
  getUserCurriculumPrograms,
  getDemoOrganizationCurriculumProgram,
  getSelectedCourseId,
  getSelectedCourseName,
  getCourseId,
  getNextOrLastAcademicYearEndDate,
  getIsCurrentAcademicYearSelected,
  getIsFutureAcademicYearSelected,
  getIsPreviousAcademicYearSelected,
  getAcademicYearId,
  getSelectedAcademicYearObject,
  getIsCurrentAndSelectedAcademicYearSame,
  getIsCommunity,
  getIsArchivedClass,
  getIsUserOnline,
  getIsChildBlocked,
  getChildProfileImage,
  getOrganizationName,
  getUserArchivalStatus,
  getImmediatePreviousAcademicYear,
  getImmediateFutureAcademicYear,
  getCurrentCurriculumContextForGlobalSearch,
  getCurrentCurriculumContextForJumpTo,
  getSelectedGrades,
  getCurrentPlannerElementSets,
  getSelectedCurriculumLabel,
  getAcademicYearForCurriculumProgram,
  getIsUserImpersonated,
  getCurriculumProgramIdFromCurrentAdminNavigationNode,
  getIBISRegistrationScreenId,
  getIBISRegistrationActiveScreenName,
  getActivePageHeaderTab,
  getCurriculumProgramIdFromRoute,
  getSelectedPrimaryGradeIdForLearningCourse,
  getSelectedPrimaryGradeIdForCourse,
  getSelectedCurriculumExamSession,
  getIsExamSessionBasedOnCurriculum,
  getExamSessionMonth,
  getUserPrimaryGradeId,
};
